import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Paperclip, Download } from "lucide-react";
import FlagImg from "../../components/agent-tender/FlagImg";
import SearchRequestStatusChip from "../../components/club-network/SearchRequestStatusChip";
import SpecRequirements from "../../components/club-network/SpecRequirements";
import ClubAvatar from "../../components/club-network/v2/ClubAvatar";
import InfoDot from "../../components/common/InfoDot";
import { trustedAgentService } from "../../services/clubNetworkApi";
import { formatCurrencyWithSymbol } from "../../utils/currencyFormatter";
import {
  formatRequestTitle,
  formatSecondary,
  formatRequestMeta,
  formatSentDate,
  formatRelativeDate,
  formatTerms,
  formatTermsShort,
  formatDeadlineCountdown,
} from "../../utils/searchRequestFormat";

// N3 Agent Inbox — Commissioned Search (QCS-T7 agent UI). The Redesign-2.0 N3
// inbox (accept/decline + club-visible cards) is EXTENDED here into the full B7
// agent overview: the tab set becomes All / To answer / Searching / Delivered /
// Archive (driven by the BE `agent_bucket`), and each bucket renders its own
// contextual row/card:
//   • to_answer  → B8 "request in full": deadline banner, full terms block,
//                  transparency line, Accept / Decline (unchanged wiring).
//   • searching  → B1 post-accept commission card (status 'accepted', not yet
//                  posted → "Post your Search Request") OR B4 searching row
//                  (status 'searching' → "[N] accepted · [N] replies" + Deliver).
//   • delivered  → B4 delivered row: "Delivered · N + date", closed to new
//                  replies, Open club chat (or "Nothing found").
//   • archive    → B5 loser ("another agent accepted first"), B10 club-closed-
//                  while-searching, declined-with-reason, B11 expired.
// Accept/Decline appear ONLY in the To-answer request-in-full view, never in the
// compact overview rows (Annex B7). Deliver wires to the T5 flow, "Post your
// Search Request" wires to the T3 prefill hand-over on the existing form (B2).

// N3·6e — S5 envelope glyph, copied verbatim from the handoff (Rule 2).
const EnvelopeGlyph = () => (
  <svg width="20" height="20" viewBox="0 0 18 18" fill="none" aria-hidden="true">
    <rect x="2" y="4" width="14" height="10" rx="2" stroke="#9CA3AF" strokeWidth="1.6" fill="none" />
    <path d="M2 6l7 4 7-4" stroke="#9CA3AF" strokeWidth="1.6" strokeLinejoin="round" fill="none" />
  </svg>
);

// Verbatim clock glyph for the B8 deadline banner.
const ClockGlyph = ({ className = "" }) => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" className={className} aria-hidden="true">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 7v5l3.2 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Decline-with-reason modal — existing function, presentation untouched.
const DeclineModal = ({ onCancel, onConfirm, busy }) => {
  const [reason, setReason] = useState("");
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/45 p-4">
      <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl p-6 flex flex-col gap-3">
        <span className="text-[15px] font-bold text-[#111827]">Decline this request?</span>
        <span className="text-[13px] text-gray-500">
          You can add a short reason for the club (optional).
        </span>
        <textarea
          rows={3}
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Outside my markets right now."
          className="w-full border border-gray-200 rounded-lg p-3 text-[13px] focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
        <div className="flex justify-end gap-2 pt-1">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-200 rounded-lg text-[13px] font-semibold hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={() => onConfirm(reason)}
            disabled={busy}
            className="px-4 py-2 bg-[#111827] text-white rounded-lg text-[13px] font-semibold hover:bg-black disabled:opacity-50"
          >
            {busy ? "Declining…" : "Decline"}
          </button>
        </div>
      </div>
    </div>
  );
};

// Attached document (the PDF brief) — reachable from the request view (B8) and,
// once accepted, pinned in the commission card (B1).
const DocumentButton = ({ onOpen, busy, label = "Attached document" }) => (
  <button
    type="button"
    onClick={onOpen}
    disabled={busy}
    className="inline-flex items-center gap-1.5 self-start bg-white border border-[#E5E7EB] rounded-lg px-2.5 py-1.5 text-[11.5px] font-semibold text-[#111827] hover:bg-gray-50 disabled:opacity-50"
  >
    <Paperclip className="w-3.5 h-3.5 text-[#2283FF]" />
    {busy ? "Opening…" : label}
    <Download className="w-3.5 h-3.5 text-gray-400" />
  </button>
);

const flagClass = "w-4 h-[11.5px] rounded-[2px]";

const NewChip = () => (
  <span className="ml-auto flex-none rounded-full bg-[#E8F1FF] text-[#2283FF] font-bold text-[10.5px] md:text-[11px] px-[9px] py-0.5 md:px-2.5 md:py-[3px]">
    New
  </span>
);

// ── Terms label maps (reused from the club create form, CreateSearchRequest.js
// A10-A12) so the agent reads the exact same wording the club set. ──
const PERIOD_LABEL = { per_year: "per year", per_month: "per month", per_week: "per week" };
const BASIS_LABEL = { gross: "gross", net: "net" };
const COMMISSION_BASIS_LABEL = {
  percentage_of_salary: "of player salary",
  percentage_of_transfer: "of transfer fee",
  fixed_fee: "fixed",
};
const PAYOUT_LABEL = {
  one_total: "One commission in total — the invited agents divide it",
  each_separate: "Each agent paid separately",
};
const money = (amt, cur) => (amt ? formatCurrencyWithSymbol(amt, cur || "EUR") : "");

// B8 full terms block — budget, salary (gross/net), commission (incl. payout
// mode + note), loan terms. Reads the flat term fields the BE persists (T6).
// Renders nothing when the club attached no terms (the request works without
// them, Annex A10).
const TermsBlock = ({ item }) => {
  if (!item.terms_enabled) return null;
  const cur = item.currency || "EUR";
  const isStaff = item.tender_category === "staff";
  const rows = [];

  // Budget / free agents.
  if (item.only_free_players) rows.push(["Budget", "Free agents only"]);
  else if (item.maximum_transfer_fee)
    rows.push(["Budget", `Transfer budget up to ${money(item.maximum_transfer_fee, cur)}`]);

  // Salary (gross/net).
  if (item.salary_amount) {
    const period = PERIOD_LABEL[item.salary_period] || "per year";
    const basis = BASIS_LABEL[item.salary_basis];
    rows.push([
      "Salary",
      `${money(item.salary_amount, cur)} ${period}${basis ? ` (${basis})` : ""}`,
    ]);
  }

  // Commission (basis + payout mode + note).
  if (item.commission_value) {
    let val;
    if (item.commission_basis === "fixed_fee") {
      val = `${money(item.commission_value, cur)} (fixed)`;
    } else {
      const basis =
        isStaff && item.commission_basis === "percentage_of_salary"
          ? "of salary"
          : COMMISSION_BASIS_LABEL[item.commission_basis] || "";
      val = `${item.commission_value}% ${basis}`.trim();
    }
    rows.push(["Commission", val]);
    if (item.commission_payout_mode && PAYOUT_LABEL[item.commission_payout_mode])
      rows.push(["Payout", PAYOUT_LABEL[item.commission_payout_mode]]);
  }

  // Loan terms (only meaningful on a loan request).
  if (item.transfer_type === "loan") {
    rows.push([
      "Loan fee",
      item.loan_fee_enabled && item.loan_fee_amount
        ? `${money(item.loan_fee_amount, cur)} for the season`
        : "No loan fee",
    ]);
    if (item.loan_option_to_buy) rows.push(["Option", "Option to buy wanted"]);
  }

  if (!rows.length) return null;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-[10px] px-[13px] py-3 flex flex-col gap-[7px]">
      <span className="text-[11.5px] font-bold text-[#111827] uppercase tracking-[.03em]">
        Terms they offer
      </span>
      <div className="flex flex-col gap-[5px]">
        {rows.map(([label, value]) => (
          <div key={label} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[12px]">
            <span className="text-[#6B7280] font-medium w-[74px] flex-none">{label}</span>
            <span className="text-[#111827] font-semibold min-w-0">{value}</span>
          </div>
        ))}
      </div>
      {item.commission_note && (
        <span className="text-[11.5px] text-[#4B5563] italic leading-[1.5] border-t border-[#F1F5F9] pt-[7px]">
          "{item.commission_note}"
        </span>
      )}
    </div>
  );
};

// B8 — the club's personal note, quoted (A0-2/B8/E7). One club-written note that
// travels to every invited agent; it is club-entered free text, so it is
// rendered as a QUOTE, never as an FE-authored sentence. Shown only when set,
// separate from the spec free-text and the terms note (its own BE field).
const PersonalNote = ({ note }) => {
  if (!note || !note.trim()) return null;
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-[10px] px-[13px] py-3 flex flex-col gap-1.5">
      <span className="text-[11.5px] font-bold text-[#111827] uppercase tracking-[.03em]">
        The club's personal note
      </span>
      <span className="text-[12px] text-[#4B5563] italic leading-[1.55] whitespace-pre-line break-words">
        "{note.trim()}"
      </span>
    </div>
  );
};

// B8 deadline banner — "Answer within N days, [countdown] left · No answer
// counts as a decline". Countdown recomputes from the loaded remaining seconds
// against a live tick (M·B8: it sits on top, in the thumb's eyeline). Hidden
// when the club set no deadline (remaining is null, B9 "3/5/7 days or none").
const DeadlineBanner = ({ item, tick, mountedAt }) => {
  if (item.deadline_remaining_seconds == null) return null;
  const elapsed = (Date.now() - mountedAt) / 1000;
  const remaining = item.deadline_remaining_seconds - elapsed;
  const left = formatDeadlineCountdown(remaining);
  const withinDays = item.answer_deadline_days;
  return (
    <div
      className="flex items-start gap-2 bg-[#FFF7ED] border border-[#FED7AA] rounded-[10px] px-3 py-2.5"
      data-tick={tick}
    >
      <ClockGlyph className="text-[#C2410C] flex-none mt-[1px]" />
      <span className="text-[12px] text-[#9A3412] leading-[1.5]">
        {withinDays ? (
          <>
            <b>{`Answer within ${withinDays} days`}</b>
            {", "}
            {`${left} left`}
          </>
        ) : (
          <>
            <b>{`${left} left`}</b>
            {" to answer"}
          </>
        )}
        {" · "}
        {"No answer counts as a decline"}
      </span>
    </div>
  );
};

// B8 transparency line — "[N] other agents were invited. The first to accept
// gets the job." The count is the OTHER invited agents: invited_count (all
// recipients on the request, from the record) minus this agent. Rendered as a
// concrete singular/plural sentence (the repo's DOM-i18n matches the rendered
// string; Part B registers both variants) — never string-glued from a raw
// number the FE typed. When this agent is the only invitee (others < 1) the
// line is omitted rather than showing "0 other agents".
const TransparencyLine = ({ item }) => {
  const others = (item.invited_count || 0) - 1;
  if (others < 1) return null;
  return (
    <span className="text-[11.5px] text-[#6B7280] leading-[1.5]">
      {others}{" "}
      {others === 1
        ? "other agent was invited."
        : "other agents were invited."}{" "}
      The first to accept gets the job.
    </span>
  );
};

// Club header row (identity fully visible on this seam — Annex B8/global rule).
// The sender line reads "sent [date] by [name, role]" (Annex B8): name + role
// come from the commissioning club member's record via the serializer `sender`
// {name, role} — never free-typed. The "by [name, role]" tail renders only when
// the record carries a name; the static words ("sent", "by") sit in their own
// text nodes so the DOM-i18n translates them while the data stays untouched. The
// line wraps (flex-wrap) so a long name + role never overflows at 375.
const ClubHead = ({ item, chip }) => {
  const sent = formatSentDate(item);
  const senderName = (item.sender?.name || "").trim();
  const senderRole = (item.sender?.role || "").trim();
  const senderLabel = senderRole ? `${senderName}, ${senderRole}` : senderName;
  return (
    <div className="flex items-center gap-2.5 md:gap-[11px]">
      <ClubAvatar club={item.club} className="w-[38px] h-[38px] md:w-10 md:h-10 text-[13px]" />
      <div className="flex flex-col md:gap-[1px] flex-1 min-w-0">
        <span className="text-[13.5px] md:text-[14px] font-bold text-[#111827] truncate">
          {item.club?.name}
        </span>
        <span className="flex flex-wrap items-center gap-x-[5px] gap-y-0.5 text-[11px] md:text-[11.5px] text-[#6B7280]">
          <FlagImg country={item.club?.country} className={flagClass} />
          {item.club?.country && <span>{item.club.country}</span>}
          {item.club?.country && sent && <span>·</span>}
          {sent && (
            <span>
              <span>sent</span> <span>{sent}</span>
            </span>
          )}
          {senderName && (
            <span className="min-w-0">
              <span>by</span> <span className="font-medium text-[#4B5563]">{senderLabel}</span>
            </span>
          )}
        </span>
      </div>
      {chip}
    </div>
  );
};

// Spec panel (shared by the full B8 card).
const SpecPanel = ({ item, children }) => {
  const secondary = formatSecondary(item);
  const terms = formatTerms(item);
  const termsShort = formatTermsShort(item);
  return (
    <div className="bg-[#F8FAFC] border border-[#E5E7EB] rounded-[10px] px-[13px] py-3 md:px-[15px] md:py-[13px] flex flex-col gap-[5px] md:gap-1.5">
      <span className="text-[13px] md:text-[13.5px] font-bold text-[#111827]">
        {formatRequestTitle(item)}
        {secondary && (
          <span className="font-normal text-[#6B7280] text-[11.5px] md:text-[12px]">
            {" "}
            · also {secondary}
          </span>
        )}
      </span>
      <span className="text-[11.5px] md:text-[12px] text-[#6B7280] leading-[1.5]">
        {formatRequestMeta(item)}
      </span>
      {terms && (
        <span className="hidden md:inline-block self-start text-[12px] text-[#4B5563] bg-white border border-[#E5E7EB] px-2.5 py-1 rounded-lg">
          <span>Terms:</span> {terms}
        </span>
      )}
      {termsShort && (
        <span className="md:hidden self-start inline-block text-[11.5px] text-[#4B5563] bg-white border border-[#E5E7EB] px-[9px] py-[3px] rounded-lg">
          {termsShort}
        </span>
      )}
      <SpecRequirements request={item} className="mt-0.5" />
      {children}
    </div>
  );
};

// B8 — the request in full: deadline banner on top, club, spec, full terms
// block, transparency line, then Accept / Decline (unchanged wiring).
const NewRequestCard = ({
  item,
  tick,
  mountedAt,
  onAccept,
  onDecline,
  onOpenDoc,
  docBusy,
  busy,
}) => (
  <div className="bg-white border-[1.5px] border-[#BFDBFE] rounded-[14px] md:rounded-xl p-[15px] md:px-5 md:py-[18px] flex flex-col gap-[11px] md:gap-3 shadow-[0_2px_8px_rgba(34,131,255,0.08)]">
    {/* M·B8: the countdown is the first thing on the card, above everything. */}
    <DeadlineBanner item={item} tick={tick} mountedAt={mountedAt} />
    <ClubHead item={item} chip={<NewChip />} />
    <SpecPanel item={item}>
      {item.has_document && <DocumentButton onOpen={() => onOpenDoc(item)} busy={docBusy} />}
    </SpecPanel>
    <TermsBlock item={item} />
    <PersonalNote note={item.personal_note} />
    <TransparencyLine item={item} />
    {/* Actions — inline on desktop, stacked full-width in thumb reach on mobile. */}
    <div className="flex flex-col md:flex-row md:items-center md:flex-wrap gap-2">
      <button
        onClick={() => onAccept(item)}
        disabled={busy}
        className="w-full md:w-auto bg-[#2283FF] text-white border-none rounded-[11px] md:rounded-lg text-[14px] md:text-[13px] font-semibold p-[13px] md:px-4 md:py-2.5 hover:bg-[#1B6FE0] disabled:opacity-50"
      >
        Accept and start searching
      </button>
      <button
        onClick={() => onDecline(item)}
        disabled={busy}
        className="w-full md:w-auto bg-white text-[#111827] border border-[#E5E7EB] rounded-[11px] md:rounded-lg text-[13.5px] md:text-[13px] font-semibold p-3 md:px-4 md:py-2.5 hover:bg-[#F3F4F6] disabled:opacity-50"
      >
        Decline
      </button>
      <span className="text-[10.5px] md:text-[11.5px] text-[#9CA3AF] text-center md:text-left">
        Accepting opens a chat with the club.
      </span>
    </div>
  </div>
);

// Compact overview row shell — used by every non-to_answer bucket and by the
// All tab's to_answer entries. Club + spec on the left, a chip + a contextual
// action block on the right. Rows wrap (never truncate the chip) per the
// translation-safety rule.
const OverviewRow = ({ item, chip, subline, action, muted = false }) => {
  const title = formatRequestTitle(item);
  return (
    <div
      className={`bg-white border border-[#E5E7EB] rounded-[14px] md:rounded-xl px-[15px] py-3.5 md:px-5 md:py-4 flex flex-col md:flex-row md:items-center gap-2.5 md:gap-3 ${
        muted ? "opacity-[0.9]" : ""
      }`}
    >
      <ClubAvatar club={item.club} className="w-[34px] h-[34px] md:w-9 md:h-9 text-[12px] flex-none" />
      <div className="flex flex-col gap-0.5 flex-1 min-w-0">
        <span className="flex flex-wrap items-center gap-[7px] md:gap-2">
          <span className="text-[13px] md:text-[13.5px] font-semibold text-[#111827] truncate max-w-full">
            {item.club?.name}
          </span>
          {chip}
        </span>
        <span className="inline-flex items-center gap-[5px] text-[11.5px] text-[#6B7280] min-w-0">
          <FlagImg country={item.club?.country} className={flagClass} />
          {item.club?.country && <span>{item.club.country}</span>}
          {item.club?.country && <span>·</span>}
          <span className="truncate">{title}</span>
        </span>
        {subline}
      </div>
      {action && <div className="flex-none w-full md:w-auto">{action}</div>}
    </div>
  );
};

const PillButton = ({ onClick, children, primary = false }) => (
  <button
    onClick={onClick}
    className={`w-full md:w-auto rounded-lg text-[12.5px] font-semibold px-[13px] py-2 ${
      primary
        ? "bg-[#2283FF] text-white hover:bg-[#1B6FE0]"
        : "bg-white text-[#111827] border border-[#E5E7EB] hover:bg-[#F3F4F6]"
    }`}
  >
    {children}
  </button>
);

// B1 — post-accept commission card. The agent accepted but has not yet posted
// the commissioned request; this prompts him to post it (→ the B2 hand-over).
// It is the simplest clean placement of the B1 content (see Lead report: no
// pinned-above-composer mechanism in the shared GetStream chat).
const CommissionCard = ({ item, onPost, onOpenDoc, docBusy }) => (
  <div className="bg-white border-[1.5px] border-[#BFDBFE] rounded-[14px] md:rounded-xl p-[15px] md:px-5 md:py-[18px] flex flex-col gap-[11px] shadow-[0_2px_8px_rgba(34,131,255,0.08)]">
    <ClubHead item={item} />
    {/* The chips sit on their OWN line (they are long) — they wrap, never
        squeeze the club identity line on 375 (UI rule 1: wrap, never squeeze).
        The amber "not posted yet" chip makes the gap explicit under the
        Searching tab: the commission is accepted but the request is not live,
        so nothing has come in — the post step is still missing. */}
    <div className="flex flex-wrap items-center gap-2">
      <span className="flex-none rounded-full bg-[#FEF3C7] text-[#B45309] font-bold text-[10.5px] md:text-[11px] px-[9px] py-0.5 md:px-2.5 md:py-[3px]">
        Accepted · not posted yet
      </span>
      <span className="flex-none rounded-full bg-[#E8F1FF] text-[#2283FF] font-bold text-[10.5px] md:text-[11px] px-[9px] py-0.5 md:px-2.5 md:py-[3px]">
        No expiry while searching
      </span>
      <span className="text-[11.5px] text-[#6B7280]">Search Request #{item.id}</span>
    </div>
    <span className="text-[12.5px] text-[#374151] leading-[1.55]">
      <b>{item.club?.name}</b> commissioned you to search — post the request to
      start. It goes live <b>Club Verified</b>.
    </span>
    {item.has_document && (
      <div className="flex flex-col gap-1">
        <DocumentButton onOpen={() => onOpenDoc(item)} busy={docBusy} label="Open the brief (PDF)" />
        <span className="text-[10.5px] text-[#9CA3AF]">The brief stays reachable here.</span>
      </div>
    )}
    <div className="flex flex-col md:flex-row gap-2">
      <button
        onClick={() => onPost(item)}
        className="w-full md:w-auto bg-[#2283FF] text-white rounded-[11px] md:rounded-lg text-[14px] md:text-[13px] font-semibold p-[13px] md:px-4 md:py-2.5 hover:bg-[#1B6FE0]"
      >
        Post your Search Request
      </button>
    </div>
  </div>
);

// ── Overview bucket rows ─────────────────────────────────────────────────────
// B4 searching (live) — counts + Deliver. Null-guarded: before the request is
// posted both counts are 0 (never a crash / never a lie).
const SearchingRow = ({ item, onDeliver, onViewRequest }) => {
  const accepted = item.accepted_players_count || 0;
  const replies = item.replies_count || 0;
  // The replies live on the agent's OWN request (MyAgentTenders → Responses).
  // "View request · N replies" deep-links there, scoped to this commission —
  // but only once the request is posted (commissioned_tender_id present);
  // before that there is nothing to link to, so the action is hidden (never a
  // broken navigate). Both buttons stack full-width on 375, sit inline ≥md.
  const canView = item.commissioned_tender_id != null;
  return (
    <OverviewRow
      item={item}
      chip={<SearchRequestStatusChip status="searching" />}
      subline={
        <span className="text-[11.5px] text-[#374151] font-medium">
          {`${accepted === 1 ? "1 player" : `${accepted} players`} accepted · ${
            replies === 1 ? "1 reply" : `${replies} replies`
          }`}
        </span>
      }
      action={
        <div className="flex flex-col md:flex-row gap-2">
          {canView && (
            <PillButton onClick={() => onViewRequest(item)}>
              {replies === 1
                ? "View request · 1 reply"
                : `View request · ${replies} replies`}
            </PillButton>
          )}
          <PillButton primary onClick={() => onDeliver(item)}>
            Deliver to club
          </PillButton>
        </div>
      }
    />
  );
};

// B4 delivered — "Delivered · N + date", closed to new replies, Open club chat.
// A zero delivered_count is the honest empty path → "Nothing found" chip.
const DeliveredRow = ({ item, onOpenChat }) => {
  const n = item.delivered_count;
  const emptyEnd = n === 0;
  return (
    <OverviewRow
      item={item}
      chip={
        emptyEnd ? (
          <SearchRequestStatusChip status="nothing_found" />
        ) : (
          <SearchRequestStatusChip status="delivered" count={n} />
        )
      }
      subline={
        <span className="text-[11.5px] text-[#6B7280]">
          {emptyEnd ? "Nothing found" : "Delivered"}
          {item.responded_at && <> · {formatRelativeDate(item.responded_at)}</>}
          {" · "}
          {"closed to new replies"}
        </span>
      }
      action={
        <PillButton onClick={() => onOpenChat(item)}>Open club chat</PillButton>
      }
    />
  );
};

// B5 — loser of the accept race. Another agent accepted first; nothing to do.
// Distinguished from a club-close (B10) by the serializer `row_resolution`,
// which the BE fills ("Closed, [winner] was first") ONLY when another recipient
// actually holds the commission — see renderItem. When present it carries the
// winner's name (from the winning recipient's record), rendered verbatim as the
// B5/A1 line; the FE-authored "Nothing to do here." stays a separate, translated
// text node. Falls back to a count-free sentence if the record has no winner
// name.
const LoserRow = ({ item }) => (
  <OverviewRow
    item={item}
    muted
    chip={<SearchRequestStatusChip status="closed" />}
    subline={
      <span className="text-[11.5px] text-[#6B7280]">
        {item.row_resolution ? (
          <span>{item.row_resolution}. </span>
        ) : (
          "Another agent accepted first. "
        )}
        Nothing to do here.
      </span>
    }
  />
);

// B10 — the club closed the commission while the agent was searching. Mirrored
// wording ("the club closed it"); the accepted players stay in the agent's own
// list; the chat switches to the standard 5-day debrief timer.
const ClubClosedRow = ({ item, onOpenChat }) => (
  <OverviewRow
    item={item}
    muted
    chip={<SearchRequestStatusChip status="closed" />}
    subline={
      <div className="flex flex-col gap-1">
        <span className="text-[11.5px] text-[#6B7280] leading-[1.5]">
          The club ended the commission. Your request came down from the network
          and takes no new replies. The players you already accepted stay in your
          own list.
        </span>
        <span className="inline-flex items-center gap-1.5 self-start text-[10.5px] font-semibold text-[#B45309] bg-[#FEF3C7] rounded-full px-2 py-0.5">
          Chat expires in 5 days
        </span>
      </div>
    }
    action={<PillButton onClick={() => onOpenChat(item)}>Open club chat</PillButton>}
  />
);

// Archive — declined (with the quoted reason), B11 expired ("nobody answered"),
// or a plain club-close of an unanswered invitation.
const ArchiveRow = ({ item }) => {
  const rs = item.recipient_status;
  if (rs === "declined") {
    return (
      <OverviewRow
        item={item}
        muted
        chip={<SearchRequestStatusChip status="declined" />}
        subline={
          item.decline_reason ? (
            <span className="text-[11.5px] text-[#6B7280] italic truncate">
              "{item.decline_reason}"
            </span>
          ) : null
        }
      />
    );
  }
  if (rs === "expired") {
    return (
      <OverviewRow
        item={item}
        muted
        chip={<SearchRequestStatusChip status="expired" />}
        subline={
          <span className="text-[11.5px] text-[#6B7280]">
            {`Nobody answered — the ${item.answer_deadline_days || 5}-day deadline passed.`}
          </span>
        }
      />
    );
  }
  // Plain closed (unanswered invitation withdrawn/closed by the club).
  return (
    <OverviewRow
      item={item}
      muted
      chip={<SearchRequestStatusChip status="closed" />}
      subline={
        <span className="text-[11.5px] text-[#6B7280]">
          The club closed this request.
        </span>
      }
    />
  );
};

// B7 tab set, display order. "All" is the union; the other four map to the BE
// `agent_bucket` (to_answer / searching / delivered / archive).
const B7_TABS = [
  { key: "all", label: "All" },
  { key: "to_answer", label: "To answer" },
  { key: "searching", label: "Searching" },
  { key: "delivered", label: "Delivered" },
  { key: "archive", label: "Archive" },
];

// Bucket resolution. Trusts the BE `agent_bucket`, with two FE overrides that
// the status→bucket map cannot see on its own:
//   • a still-"sent"/"seen" invitation on a CLOSED request is not actionable
//     (accept 400s) → archive, never "To answer" (the W1-07 lesson).
const FALLBACK_BUCKET = {
  sent: "to_answer",
  seen: "to_answer",
  accepted: "searching",
  searching: "searching",
  delivered: "delivered",
  nothing_found: "delivered",
  declined: "archive",
  expired: "archive",
  closed: "archive",
};
const bucketOf = (i) => {
  const rs = i.recipient_status;
  if ((rs === "sent" || rs === "seen") && i.status === "closed") return "archive";
  return i.agent_bucket || FALLBACK_BUCKET[rs] || "archive";
};

const AgentSearchInbox = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [declining, setDeclining] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [docBusyId, setDocBusyId] = useState(null);
  const [actionError, setActionError] = useState(null);
  const initialTab =
    new URLSearchParams(window.location.search).get("tab") || "all";
  const [activeTab, setActiveTab] = useState(
    B7_TABS.some((t) => t.key === initialTab) ? initialTab : "all",
  );

  // Live-ish countdown for the B8 deadline banner: recompute against a 60s tick
  // from the moment the list mounted (no per-second churn, still counts down).
  const mountedAtRef = useRef(Date.now());
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60000);
    return () => clearInterval(id);
  }, []);

  const openDoc = async (item) => {
    setDocBusyId(item.id);
    const res = await trustedAgentService.getDocumentUrl(item.id);
    setDocBusyId(null);
    if (res.success && res.data?.download_url) {
      window.open(res.data.download_url, "_blank", "noopener,noreferrer");
    }
  };

  const load = () => {
    setLoading(true);
    setError(null);
    trustedAgentService.listInbox().then((res) => {
      if (res.success) setItems(res.data);
      else setError(res.error || "Failed to load your search requests.");
      setLoading(false);
    });
  };
  useEffect(load, []);

  // A14 Seen (B8): opening the request in full marks it seen. The To-answer tab
  // IS the request-in-full seam, so each still-pending (not club-closed) request
  // is marked seen once it renders. Idempotent + one-directional server-side.
  const seenFiredRef = useRef(new Set());
  useEffect(() => {
    items
      .filter((i) => i.recipient_status === "sent" && i.status !== "closed")
      .forEach((i) => {
        if (seenFiredRef.current.has(i.id)) return;
        seenFiredRef.current.add(i.id);
        trustedAgentService.markSeen?.(i.id);
      });
  }, [items]);

  const openChat = (item) => {
    if (item.chat_channel_id) navigate(`/inbox?channel=${item.chat_channel_id}`);
    else navigate("/inbox");
  };

  const deliver = (item) => navigate(`/network/search-requests/${item.id}/deliver`);

  // "View request · N replies" — the agent's own posted request lives under
  // MyAgentTenders; its replies are the Responses tab. Deep-link there scoped to
  // this commission's Tender (?tender=<id>). Guarded on the id (the row hides the
  // action when it is absent) so this never fires an id-less navigate.
  const viewRequestResponses = (item) => {
    if (item.commissioned_tender_id == null) return;
    navigate(
      `/agent-tenders/mine?tab=responses&tender=${item.commissioned_tender_id}`,
    );
  };

  // B2 hand-over: "Post your Search Request" → the EXISTING Agent Request form
  // with the T3 prefill. The commissioning club (fully visible on this seam)
  // travels in router state as a ClubPicker-shaped value; the form fetches the
  // locked criteria + verified-from-birth by the search-request id.
  const postRequest = (item) => {
    const commissionClub = {
      name: item.club?.name,
      country: item.club?.country,
      logo: item.club?.logo,
      image_path: item.club?.logo,
      platform_data: { exists: true, club_id: item.club?.id },
    };
    navigate(`/agent-tenders/create?commission=${item.id}`, {
      state: { commissionClub, searchRequestId: item.id },
    });
  };

  const accept = async (item) => {
    setActionError(null);
    setBusyId(item.id);
    const res = await trustedAgentService.acceptInbox(item.id);
    setBusyId(null);
    if (res.success) {
      const channelId = res.data?.channel?.channel_id;
      setItems((prev) =>
        prev.map((x) =>
          x.id === item.id
            ? {
                ...x,
                recipient_status: "accepted",
                agent_bucket: "searching",
                responded_at: new Date().toISOString(),
                chat_channel_id: channelId,
              }
            : x,
        ),
      );
      setActiveTab("searching");
      if (channelId) navigate(`/inbox?channel=${channelId}`);
    } else {
      setActionError(res.error || "Something went wrong. Please try again.");
      if (res.status === 400) load();
    }
  };

  const confirmDecline = async (reason) => {
    const item = declining;
    setActionError(null);
    setBusyId(item.id);
    const res = await trustedAgentService.declineInbox(item.id, reason);
    setBusyId(null);
    setDeclining(null);
    if (res.success) {
      setItems((prev) =>
        prev.map((x) =>
          x.id === item.id
            ? {
                ...x,
                recipient_status: "declined",
                agent_bucket: "archive",
                decline_reason: reason,
              }
            : x,
        ),
      );
    } else {
      setActionError(res.error || "Something went wrong. Please try again.");
      if (res.status === 400) load();
    }
  };

  const counts = useMemo(() => {
    const c = { all: items.length, to_answer: 0, searching: 0, delivered: 0, archive: 0 };
    items.forEach((i) => {
      c[bucketOf(i)] += 1;
    });
    return c;
  }, [items]);

  const visible = useMemo(
    () => (activeTab === "all" ? items : items.filter((i) => bucketOf(i) === activeTab)),
    [items, activeTab],
  );

  // Render one item at its bucket-appropriate row. In the To-answer TAB a
  // to_answer item is the full B8 request-in-full (Accept/Decline live here); in
  // the All tab it is a compact row whose only action is "Open" → the To-answer
  // view (Accept/Decline never appear in the overview list, Annex B7).
  const renderItem = (item) => {
    const bucket = bucketOf(item);
    if (bucket === "to_answer") {
      if (activeTab === "to_answer") {
        return (
          <NewRequestCard
            key={item.id}
            item={item}
            tick={tick}
            mountedAt={mountedAtRef.current}
            onAccept={accept}
            onDecline={setDeclining}
            onOpenDoc={openDoc}
            docBusy={docBusyId === item.id}
            busy={busyId === item.id}
          />
        );
      }
      return (
        <OverviewRow
          key={item.id}
          item={item}
          chip={<NewChip />}
          subline={
            item.deadline_remaining_seconds != null ? (
              <span className="text-[11.5px] text-[#C2410C] font-medium">
                {`${formatDeadlineCountdown(item.deadline_remaining_seconds)} left`}
                {" "}
                {"to answer"}
              </span>
            ) : null
          }
          action={<PillButton primary onClick={() => setActiveTab("to_answer")}>Open</PillButton>}
        />
      );
    }
    if (bucket === "searching") {
      // 'accepted' but not yet posted → B1 post-your-request card; 'searching'
      // (live) → B4 counts + Deliver.
      if (item.recipient_status === "accepted") {
        return (
          <CommissionCard
            key={item.id}
            item={item}
            onPost={postRequest}
            onOpenDoc={openDoc}
            docBusy={docBusyId === item.id}
          />
        );
      }
      return (
        <SearchingRow
          key={item.id}
          item={item}
          onDeliver={deliver}
          onViewRequest={viewRequestResponses}
        />
      );
    }
    if (bucket === "delivered") {
      return <DeliveredRow key={item.id} item={item} onOpenChat={openChat} />;
    }
    // archive
    if (item.recipient_status === "closed") {
      // B5 vs B10 come from the serializer `row_resolution`, not the request
      // status: the BE sets it ("Closed, [winner] was first") ONLY when another
      // recipient holds the commission, i.e. this agent LOST the race (B5). A
      // club-close while he was searching (B10) leaves it null. This resolves the
      // T7 edge case where a lost-race row whose request later got closed by the
      // club would wrongly flip to the B10 "the club ended the commission" copy.
      return item.row_resolution ? (
        <LoserRow key={item.id} item={item} />
      ) : (
        <ClubClosedRow key={item.id} item={item} onOpenChat={openChat} />
      );
    }
    return <ArchiveRow key={item.id} item={item} />;
  };

  return (
    <div className="min-h-full bg-[#F8FAFC]">
      <div className="max-w-3xl mx-auto px-4 md:px-6 pt-4 md:pt-[22px] pb-6 md:pb-[26px] flex flex-col gap-3">
        <h1 className="inline-flex items-center gap-2 whitespace-nowrap self-start text-[16px] font-bold text-[#111827]">
          Search requests from clubs
          <InfoDot
            title="Search requests from clubs"
            body="Clubs send you a search request when they want you to look for a specific player profile. Answer within the deadline, and when you accept, post your Search Request to start — it goes live Club Verified. Deliver your shortlist to the club when you're ready."
          />
        </h1>

        {loading ? (
          <div className="flex justify-center py-20 text-gray-500">Loading…</div>
        ) : error ? (
          <div className="flex flex-col items-center py-20 text-center gap-3">
            <p className="text-red-500">{error}</p>
            <button onClick={load} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
              Try again
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="bg-white border border-[#D6DBE3] rounded-xl shadow-[0_8px_24px_rgba(17,24,39,0.08)] px-8 py-[38px] flex flex-col items-center gap-[11px] text-center">
            <span className="w-[50px] h-[50px] rounded-full bg-[#F3F4F6] inline-flex items-center justify-center">
              <EnvelopeGlyph />
            </span>
            <span className="text-[15px] font-bold text-[#111827]">No search requests</span>
            <span className="text-[12.5px] text-[#6B7280] leading-[1.6] max-w-sm">
              When a club asks you to search for a player, it lands here with the
              full spec and any terms.
            </span>
          </div>
        ) : (
          <>
            {/* B7 tab strip WITH counts — horizontal scroll on phone (M·A16). */}
            <div className="flex items-center gap-4 md:gap-[22px] border-b border-[#E2E8F0] overflow-x-auto overflow-y-hidden">
              {B7_TABS.map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setActiveTab(key)}
                  className={`pb-[9px] text-[13.5px] whitespace-nowrap border-b-2 -mb-px transition-colors inline-flex items-center gap-1.5 ${
                    activeTab === key
                      ? "border-[#2283FF] text-[#0F172A] font-bold"
                      : "border-transparent text-[#64748B] font-medium hover:text-gray-800"
                  }`}
                >
                  {label}
                  <span
                    className={
                      activeTab === key
                        ? "text-[11px] font-extrabold bg-[#2283FF] text-white rounded-full px-[7px] py-px"
                        : "text-[#94A3B8]"
                    }
                  >
                    {counts[key]}
                  </span>
                </button>
              ))}
            </div>

            {actionError && (
              <div className="bg-[#FEF2F2] border border-[#FECACA] text-[#DC2626] text-[12.5px] rounded-lg px-4 py-3">
                {actionError}
              </div>
            )}

            {visible.length === 0 ? (
              <div className="text-center py-16 rounded-2xl border border-dashed border-[#E4E9F1]">
                <p className="text-[15px] font-semibold text-[#14263D]">No requests yet</p>
                <p className="text-[13px] text-[#8A98AC] mt-1">
                  Requests for this tab appear here.
                </p>
              </div>
            ) : (
              <div className="flex flex-col gap-3">{visible.map(renderItem)}</div>
            )}
          </>
        )}
      </div>

      {declining && (
        <DeclineModal
          onCancel={() => setDeclining(null)}
          onConfirm={confirmDecline}
          busy={busyId === declining.id}
        />
      )}
    </div>
  );
};

export default AgentSearchInbox;
